import { useNavigation } from "@react-navigation/native";
import { Calendar, Pen, Trash2 } from "@tamagui/lucide-icons";
import { uuid } from "expo-modules-core";
import { observer } from "mobx-react-lite";
import React, { useCallback } from "react";
import { Pressable } from "react-native";
import { Text, XStack, YStack } from "tamagui";
import {
  type SortableItemWithId,
  SortableList,
} from "../../../core/components/SortableList";
import { sanitizeForFirestore } from "../../../modules/core/sanitizeForFirestore";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { InfoRow } from "../../../ui/InfoRow";
import { Label } from "../../../ui/Label";
import { PopoverConfirm } from "../../../ui/PopoverConfirm";
import { BusinessScheduleItemForm } from "../../schedule/BusinessScheduleItemForm";
import type {
  BusinessTemplateModel,
  BusinessTemplateScheduleItem,
} from "./BusinessTemplateModel";

const AddScheduleItemForm = observer(
  ({ template }: { template: BusinessTemplateModel }) => {
    const [showForm, setShowForm] = React.useState(false);

    return (
      <Box>
        {showForm && (
          <BusinessScheduleItemForm
            businessPlanStartingDate={null}
            onSave={async (data) => {
              if (
                (data.date === null && data.offsetInDays === null) ||
                !data.title
              ) {
                return;
              }

              await template.addBusinessScheduleItem({
                uid: uuid.v4(),
                date: data.date ?? null,
                offsetInDays: data.offsetInDays ?? null,
                emoji: data.emoji ?? null,
                title: data.title,
                description: sanitizeForFirestore(data.description ?? null),
                schedule: data.schedule ?? null,
              });

              setShowForm(false);
            }}
          />
        )}

        {!showForm && (
          <XStack gap={"$medium"}>
            <Button
              onPress={async () => {
                setShowForm(true);
              }}
            >
              Add schedule item
            </Button>
          </XStack>
        )}
      </Box>
    );
  },
);

const getCalculatedDate = (
  scheduleItem: BusinessTemplateScheduleItem,
): string => {
  if (scheduleItem.date) {
    return scheduleItem.date;
  }

  if (scheduleItem.offsetInDays !== null) {
    if (scheduleItem.offsetInDays === 0) {
      return "On event date";
    }
    if (scheduleItem.offsetInDays < 0) {
      return `${scheduleItem.offsetInDays} days before`;
    }
    return `${scheduleItem.offsetInDays} days after`;
  }

  return "N/A";
};

export const BusinessAdminTemplateSchedule = observer(
  ({ template }: { template: BusinessTemplateModel }) => {
    const navigation = useNavigation();

    const renderItem = useCallback(
      (listItem: SortableItemWithId<BusinessTemplateScheduleItem>) => {
        const scheduleItem = listItem.item;

        return (
          <Pressable
            key={scheduleItem.uid}
            onPress={() => {
              navigation.navigate("CustomerAdminTemplateScheduleItemEdit", {
                customerUid: template.data.customerUid,
                templateUid: template.uid,
                scheduleItemUid: scheduleItem.uid,
              });
            }}
          >
            <YStack gap={"$medium"}>
              <Body>{scheduleItem.title}</Body>
              <InfoRow
                items={[
                  {
                    icon: Calendar,
                    text: <Text>{getCalculatedDate(scheduleItem)}</Text>,
                  },
                  {
                    icon: Pen,
                    text: <Text cursor="pointer">Edit</Text>,
                  },
                  {
                    icon: Trash2,
                    text: (
                      <PopoverConfirm
                        placement="top-start"
                        onConfirm={() => {
                          template.deleteBusinessTemplateScheduleItem(
                            scheduleItem,
                          );
                        }}
                      >
                        <Text cursor="pointer">Delete</Text>
                      </PopoverConfirm>
                    ),
                  },
                ]}
              />
            </YStack>
          </Pressable>
        );
      },
      [template, navigation],
    );

    return (
      <YStack gap={"$medium"}>
        <Box>
          <Label>Schedule items</Label>
          <SortableList
            items={template.data.schedule}
            onChange={(items) => {
              template?.updateScheduleItemsOrder(items.map((i) => i.item));
            }}
            renderItem={renderItem}
          />
        </Box>

        <AddScheduleItemForm template={template} />
      </YStack>
    );
  },
);
